import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { PpeTemplateData } from '../../../../models/ppeTemplateData.model';
import { IhLabResult } from '../../../../models/IhLabResult.model';
import { SampleTypeEnum } from '../../../../enums/sampleType.enum';
import { RatingTypeEnum } from '../../../../enums/ratingTypeEnum.enum';
import { TranslateService } from '../../../../services/translate.service';

interface MaxUseWarning {
  message: string;
  isExceeded: boolean;
}

@Component({
    standalone: true,
    selector: 'app-ppe-list-widget',
    imports: [],
    templateUrl: './ppe-list-widget.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./ppe-list-widget.component.scss']
})
export class PpeListWidgetComponent implements OnInit, OnChanges {
  @Input() ppeAssignments: PpeTemplateData[] = [];
  @Input() ihLabResults: IhLabResult[] = [];
  @Input() sampleType: SampleTypeEnum = SampleTypeEnum.Air;
  @Input() isVoid: boolean = false;

  maxUseWarning: MaxUseWarning | null = null;

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    this.calculateMaxUseWarning();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ppeAssignments'] || changes['ihLabResults'] || changes['sampleType']) {
      this.calculateMaxUseWarning();
    }
  }

  private calculateMaxUseWarning(): void {
    if (!this.ihLabResults?.length) {
      this.maxUseWarning = null;
      return;
    }

    const hasEstablishedOel = this.ihLabResults.some(result => {
      const oel = this.getEffectiveOel(result);
      return Number.isFinite(oel) && oel > 0;
    });
    const hasComparableOel = this.ihLabResults.some(result => {
      const effectiveOel = this.getEffectiveOel(result);
      return Number.isFinite(effectiveOel) && effectiveOel > 0
        && result.twa != null && Number.isFinite(result.twa);
    });

    if (!hasEstablishedOel) {
      this.maxUseWarning = {
        isExceeded: false,
        message: this.translateService.getDynamicTranslation(
          'OEL is not established for this agent. Exceedance cannot be evaluated.'
        )
      };
      return;
    }

    if (!hasComparableOel) {
      this.maxUseWarning = {
        isExceeded: false,
        message: this.translateService.getDynamicTranslation(
          'TWA data is not available. Exceedance cannot be evaluated against the established OEL.'
        )
      };
      return;
    }

    const isNoise = this.sampleType === SampleTypeEnum.Noise;
    const hasPpe = isNoise ? this.hasNoisePpeAssigned() : this.hasRespiratoryPpeAssigned();

    if (!hasPpe) {
      this.maxUseWarning = {
        isExceeded: false,
        message: isNoise
          ? this.translateService.getDynamicTranslation('No hearing protection is assigned. Exceedance cannot be evaluated.')
          : this.translateService.getDynamicTranslation('No respiratory protection is assigned. Default protection factor of 1 is assumed.')
      };
      return;
    }

    const isExceeded = isNoise
      ? this.checkNoisePpeExceedance()
      : this.checkRespiratoryPpeExceedance();

    const warningMessage = isExceeded
      ? isNoise
        ? this.translateService.getDynamicTranslation('Hearing protection does not provide adequate protection based on the measured noise exposure.')
        : this.translateService.getDynamicTranslation('Respiratory protection does not provide adequate protection based on the measured exposure.')
      : isNoise
        ? this.translateService.getDynamicTranslation('Hearing protection provides adequate protection based on the measured noise exposure.')
        : this.translateService.getDynamicTranslation('Respiratory protection provides adequate protection based on the measured exposure.');

    this.maxUseWarning = { isExceeded, message: warningMessage };
  }

  private hasNoisePpeAssigned(): boolean {
    return (this.ppeAssignments || [])
      .some(ppe => ppe.ratingType === RatingTypeEnum.NRR && Number.isFinite(ppe.rating) && ppe.rating > 0);
  }

  private hasRespiratoryPpeAssigned(): boolean {
    return (this.ppeAssignments || [])
      .some(ppe => ppe.ratingType === RatingTypeEnum.ProtectionFactor && Number.isFinite(ppe.rating) && ppe.rating > 0);
  }

  private checkNoisePpeExceedance(): boolean {
    const nrrRatings = (this.ppeAssignments || [])
      .filter(ppe => ppe.ratingType === RatingTypeEnum.NRR && Number.isFinite(ppe.rating) && ppe.rating > 0)
      .map(ppe => ppe.rating);

    if (!nrrRatings.length) return false;

    const maxNrr = Math.max(...nrrRatings);
    const nrrReduction = Math.max((maxNrr - 7) / 2, 0);

    return this.ihLabResults.some(result => {
      const effectiveOel = this.getEffectiveOel(result);
      if (!Number.isFinite(effectiveOel) || effectiveOel <= 0) return false;
      if (!Number.isFinite(result.twa) || result.twa <= 0) return false;
      return result.twa - nrrReduction > effectiveOel;
    });
  }

  private checkRespiratoryPpeExceedance(): boolean {
    const maxRating = (this.ppeAssignments || [])
      .filter(ppe => ppe.ratingType === RatingTypeEnum.ProtectionFactor && Number.isFinite(ppe.rating) && ppe.rating > 0)
      .reduce((max, ppe) => Math.max(max, ppe.rating ?? 1), 1);

    return this.ihLabResults.some(result => {
      const effectiveOel = this.getEffectiveOel(result);
      if (!Number.isFinite(effectiveOel) || effectiveOel <= 0) return false;
      if (!Number.isFinite(result.twa) || result.twa <= 0) return false;
      return result.twa / effectiveOel > maxRating;
    });
  }

  private getEffectiveOel(result: IhLabResult): number {
    if (result.isOelCorrectionApplied && Number.isFinite(result.correctedOEL) && result.correctedOEL! > 0) {
      return result.correctedOEL!;
    }
    return Number.isFinite(result.oel) && result.oel! > 0 ? result.oel! : 0;
  }

  // Translation getters for template
  get unknownPpeText(): string {
    return this.translateService.getDynamicTranslation('Unknown PPE');
  }

  get brandLabel(): string {
    return this.translateService.getDynamicTranslation('Brand');
  }

  get noPpeWornText(): string {
    return this.translateService.getDynamicTranslation('No PPE was worn during the sampling');
  }

  public trackByIndex = trackByIndex;
}
